import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Loader2, UserCircle2 } from "lucide-react";
import { useEffect, useState } from "react";

import { TechnicalDetails } from "@/components/TechnicalDetails";
import { usePageTitle } from "@/lib/use-page-title";
import { apiClient } from "@/lib/api";

/**
 * Profile - view + edit your own first / last name, see email,
 * roles, and technical identifiers. Splits out from Dashboard so
 * the dashboard stays a metrics-forward landing page and the
 * profile is the single place to update your identity fields.
 */
export function ProfilePage(): JSX.Element {
  usePageTitle("Your profile");
  const queryClient = useQueryClient();
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => apiClient.getProfile(),
    retry: false,
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savedFlash, setSavedFlash] = useState(false);

  // Hydrate the form from the fetched profile the first time it
  // lands (and on refetch). Not a controlled-from-query-cache
  // pattern - the user is editing, we don't want every cache
  // invalidation to clobber their in-progress edits.
  useEffect(() => {
    if (profile.data) {
      setFirstName(profile.data.first_name ?? "");
      setLastName(profile.data.last_name ?? "");
    }
    // Only re-hydrate when the user identity itself changes (e.g.
    // after impersonation), not on every refetch - otherwise an
    // in-flight edit gets clobbered by stale cache data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.data?.user_id]);

  const save = useMutation({
    mutationFn: async () => {
      if (!profile.data) throw new Error("profile not loaded");
      return apiClient.patchUser(profile.data.user_id, {
        first_name: firstName.trim() || null,
        last_name: lastName.trim() || null,
      });
    },
    onSuccess: () => {
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2500);
      void queryClient.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const dirty =
    !!profile.data &&
    (firstName.trim() !== (profile.data.first_name ?? "") ||
      lastName.trim() !== (profile.data.last_name ?? ""));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">Your profile</h1>
        <p className="text-sm text-muted-foreground">
          Update your display name. Email comes from your login and can't be changed here.
        </p>
      </div>

      {profile.isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {profile.isError && (
        <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {profile.error instanceof Error ? profile.error.message : "Failed to load profile"}
        </div>
      )}

      {profile.data && (
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (dirty && !save.isPending) save.mutate();
            }}
            className="max-w-lg space-y-4 rounded-lg border bg-card p-5 shadow-sm"
          >
            <div className="flex items-center gap-2">
              <UserCircle2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-semibold">Identity</h2>
            </div>

            <div>
              <label
                htmlFor="p-email"
                className="mb-1 block text-xs font-medium text-muted-foreground"
              >
                Email
              </label>
              <input
                id="p-email"
                type="email"
                value={profile.data.email}
                readOnly
                className="w-full rounded-md border bg-muted px-3 py-2 text-sm text-muted-foreground"
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="p-first"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  First name
                </label>
                <input
                  id="p-first"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="Jane"
                />
              </div>
              <div>
                <label
                  htmlFor="p-last"
                  className="mb-1 block text-xs font-medium text-muted-foreground"
                >
                  Last name
                </label>
                <input
                  id="p-last"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                  placeholder="Doe"
                />
              </div>
            </div>

            {save.isError && (
              <p className="text-xs text-destructive">
                {save.error instanceof Error ? save.error.message : "Save failed"}
              </p>
            )}

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={!dirty || save.isPending}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
              >
                {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {save.isPending ? "Saving…" : "Save changes"}
              </button>
              {savedFlash && (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                  <Check className="h-3.5 w-3.5" />
                  Saved
                </span>
              )}
            </div>
          </form>

          <section className="max-w-3xl rounded-lg border bg-card p-5 shadow-sm">
            <h2 className="mb-3 text-sm font-semibold">
              Your roles{" "}
              <span className="text-xs font-normal text-muted-foreground">
                ({profile.data.roles.length})
              </span>
            </h2>
            {profile.data.roles.length === 0 ? (
              <p className="text-sm text-muted-foreground">No roles granted.</p>
            ) : (
              <>
                <ul className="space-y-2 sm:hidden">
                  {profile.data.roles.map((r) => (
                    <li
                      key={`${r.partner_id}-${r.role}`}
                      className="rounded-md border bg-background p-3 text-xs"
                    >
                      <div className="font-medium">{r.partner_name}</div>
                      <div className="mt-1 font-mono text-primary">{r.role}</div>
                    </li>
                  ))}
                </ul>
                <table className="hidden w-full text-xs sm:table">
                  <thead className="border-b text-left text-muted-foreground">
                    <tr>
                      <th className="py-2 font-normal">Partner</th>
                      <th className="py-2 font-normal">Role</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profile.data.roles.map((r) => (
                      <tr key={`${r.partner_id}-${r.role}`} className="border-b last:border-0">
                        <td className="py-2">{r.partner_name}</td>
                        <td className="py-2 font-mono text-primary">{r.role}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </section>

          <TechnicalDetails>
            <dl className="grid grid-cols-1 gap-x-4 gap-y-2 text-xs sm:grid-cols-[140px_1fr]">
              <dt className="text-muted-foreground">User ID</dt>
              <dd className="break-words font-mono">{profile.data.user_id}</dd>
              <dt className="text-muted-foreground">Cognito sub</dt>
              <dd className="break-words font-mono">{profile.data.sub}</dd>
              <dt className="text-muted-foreground">Primary partner</dt>
              <dd className="break-words">
                {profile.data.primary_partner.name}{" "}
                <span className="text-muted-foreground">({profile.data.primary_partner.path})</span>
              </dd>
            </dl>
          </TechnicalDetails>
        </>
      )}
    </div>
  );
}
